import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Modal, Button, Form, Row, Col, Card, Alert, Container } from 'react-bootstrap';
import { 
  FaPlus, FaEdit, FaTrash, FaArrowLeft, FaTimes, FaSave, FaCheck, FaPencilAlt,
  FaCheckSquare, FaListOl, FaLink, FaSort, FaFileAlt, 
  FaMicrophone, FaVideo, FaUpload, FaImage,
  FaQuestionCircle, FaCog, FaEye, FaBriefcase, FaCogs
} from 'react-icons/fa';
import api from '../../../api';
import { showSuccess, showError, showDeleteConfirmation, showWarning } from '../../../utils/sweetAlertConfig';

const AssessmentBuilder = () => {
  const { exam_id } = useParams();
  const navigate = useNavigate();

  // Check if in standalone mode (opened from popup)
  const isStandaloneMode = new URLSearchParams(window.location.search).get('standalone') === 'true';

  const [assessment, setAssessment] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [selectedQuestionType, setSelectedQuestionType] = useState('multiple_choice');

  // Title editing state
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');

  const [questionForm, setQuestionForm] = useState({
    question_text: '',
    question_type: 'multiple_choice',
    points: 1,
    question_order: 1,
    options: [
      { option_text: '', is_correct: false, option_order: 1 },
      { option_text: '', is_correct: false, option_order: 2 }
    ]
  });

  const questionTypes = [
    { 
      type: 'true_false', 
      label: 'True/False', 
      icon: FaCheckSquare, 
      color: '#28a745',
      description: 'Simple true or false questions'
    },
    { 
      type: 'multiple_choice', 
      label: 'Multiple Choice', 
      icon: FaListOl, 
      color: '#007bff',
      description: 'Questions with multiple options'
    },
    { 
      type: 'short_answer', 
      label: 'Short Answer', 
      icon: FaFileAlt, 
      color: '#6f42c1',
      description: 'Brief text responses'
    },
    { 
      type: 'essay', 
      label: 'Essay', 
      icon: FaEdit, 
      color: '#fd7e14',
      description: 'Long-form written responses'
    },
    { 
      type: 'matching', 
      label: 'Matching', 
      icon: FaLink, 
      color: '#20c997',
      description: 'Match items from two lists'
    },
    { 
      type: 'ordering', 
      label: 'Ordering', 
      icon: FaSort, 
      color: '#e83e8c',
      description: 'Arrange items in correct order'
    },
    { 
      type: 'file_upload', 
      label: 'File Upload', 
      icon: FaUpload, 
      color: '#6c757d',
      description: 'Upload documents or files'
    },
    { 
      type: 'audio', 
      label: 'Audio', 
      icon: FaMicrophone, 
      color: '#dc3545',
      description: 'Audio recording questions'
    },
    { 
      type: 'video', 
      label: 'Video', 
      icon: FaVideo, 
      color: '#ffc107',
      description: 'Video recording questions'
    },
    { 
      type: 'highlight_image', 
      label: 'Highlight Image', 
      icon: FaImage, 
      color: '#17a2b8',
      description: 'Click or highlight areas on image'
    }
  ];

  const [showImportModal, setShowImportModal] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [importErrors, setImportErrors] = useState([]);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (exam_id) {
      fetchAssessment();
      fetchQuestions();
    }
  }, [exam_id]);

  const fetchAssessment = async () => {
    try {
      const response = await api.get(`/exams/${exam_id}`);
      setAssessment(response.data);
      setEditedTitle(response.data.title);
    } catch (error) {
      showError('Failed to fetch assessment details.');
      navigate('/assessments');
    }
  };

  const handleTitleEdit = () => {
    setIsEditingTitle(true);
  };

  const handleTitleSave = async () => {
    try {
      // Send all assessment data with updated title
      const updateData = {
        title: editedTitle,
        start_date: assessment.start_date,
        end_date: assessment.end_date,
        assigned_reviewers: assessment.assigned_reviewers,
        number_of_trials: assessment.number_of_trials,
        is_timed: assessment.is_timed,
        duration_minutes: assessment.duration_minutes,
        job_id: assessment.job_id
      };
      
      await api.put(`/exams/${exam_id}`, updateData);
      setAssessment(prev => ({ ...prev, title: editedTitle }));
      setIsEditingTitle(false);
      showSuccess('Assessment title updated successfully!');
    } catch (error) {
      console.error('Title update error:', error.response?.data);
      if (error.response?.data?.errors) {
        const firstError = Object.values(error.response.data.errors)[0][0];
        showError(firstError);
      } else {
        showError('Failed to update assessment title.');
      }
    }
  };

  const handleTitleCancel = () => {
    setEditedTitle(assessment.title);
    setIsEditingTitle(false);
  };

  const handleSave = async () => {
    try {
      showSuccess('Assessment saved successfully!');
      fetchAssessment();
    } catch (error) {
      showError('Failed to save assessment.');
    }
  };

  const handlePreview = () => {
    if (questions.length === 0) {
      showError('Please add at least one question before previewing the assessment.');
      return;
    }
    
    // Open preview in new window
    const previewWindow = window.open('', '_blank', 'width=1000,height=700,scrollbars=yes,resizable=yes');
    
    const previewContent = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Assessment Preview - ${assessment?.title}</title>
          <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.1.3/dist/css/bootstrap.min.css" rel="stylesheet">
          <style>
              body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; background: #f8f9fa; }
              .assessment-header { background: linear-gradient(135deg, #29204C 0%, #3d2c61 100%); color: white; padding: 30px 0; }
              .question-card { border: none; border-radius: 15px; box-shadow: 0 5px 20px rgba(0,0,0,0.1); margin-bottom: 30px; }
              .question-number { background: #29204C; color: white; padding: 10px 15px; border-radius: 50%; font-weight: bold; margin-right: 15px; }
              .option-item { padding: 12px 20px; border: 2px solid #e9ecef; border-radius: 10px; margin-bottom: 10px; cursor: pointer; transition: all 0.3s ease; }
              .option-item:hover { border-color: #29204C; background: #f8f9fa; }
              .points-badge { background: #68B977; color: white; padding: 8px 15px; border-radius: 20px; font-weight: bold; }
              .assessment-info { background: rgba(255,255,255,0.1); padding: 20px; border-radius: 15px; margin-top: 20px; }
              .timer-warning { background: #ffc107; color: #000; padding: 15px; border-radius: 10px; margin-bottom: 20px; font-weight: bold; }
          </style>
      </head>
      <body>
          <div class="assessment-header">
              <div class="container">
                  <div class="text-center">
                      <h1 class="mb-3">${assessment?.title || 'Assessment Preview'}</h1>
                      <div class="assessment-info">
                          ${assessment?.job ? `<p class="mb-2"><strong>Position:</strong> ${assessment.job.posting_title}</p>` : ''}
                          <p class="mb-2"><strong>Total Questions:</strong> ${questions.length}</p>
                          <p class="mb-2"><strong>Total Marks:</strong> ${questions.reduce((sum, q) => sum + parseFloat(q.points || 0), 0)}</p>
                          ${assessment?.is_timed ? `<p class="mb-0"><strong>Time Limit:</strong> ${assessment.duration_minutes} minutes</p>` : '<p class="mb-0"><strong>No Time Limit</strong></p>'}
                      </div>
                  </div>
              </div>
          </div>

          <div class="container mt-4 mb-5">
              ${assessment?.is_timed ? '<div class="timer-warning text-center">⏰ This is a timed assessment. You will have ' + assessment.duration_minutes + ' minutes to complete all questions.</div>' : ''}
              
              <div class="row justify-content-center">
                  <div class="col-lg-10">
                      ${questions.map((question, index) => {
                        const questionNumber = index + 1;
                        return `
                          <div class="card question-card">
                              <div class="card-body p-4">
                                  <div class="d-flex align-items-start mb-4">
                                      <span class="question-number">${questionNumber}</span>
                                      <div class="flex-grow-1">
                                          <h5 class="mb-3">${question.question_text}</h5>
                                          <span class="marks-badge">${question.points} ${question.points === 1 ? 'Mark' : 'Marks'}</span>
                                      </div>
                                  </div>
                                  
                                  ${question.question_type === 'multiple_choice' || question.question_type === 'true_false' ? 
                                    question.options.map((option, optIndex) => `
                                      <div class="option-item">
                                          <input type="radio" name="question_${question.question_id}" id="q${question.question_id}_opt${optIndex}" class="me-3">
                                          <label for="q${question.question_id}_opt${optIndex}">${String.fromCharCode(65 + optIndex)}. ${option.option_text}</label>
                                      </div>
                                    `).join('') 
                                  : question.question_type === 'short_answer' ?
                                    '<textarea class="form-control" rows="3" placeholder="Enter your answer here..."></textarea>'
                                  : question.question_type === 'essay' ?
                                    '<textarea class="form-control" rows="8" placeholder="Write your essay here..."></textarea>'
                                  : '<div class="alert alert-info">This question type will be available in the actual assessment.</div>'
                                  }
                              </div>
                          </div>
                        `;
                      }).join('')}
                      
                      <div class="text-center mt-4">
                          <button class="btn btn-success btn-lg px-5" style="background: #68B977; border: none;" onclick="window.close()">
                              Submit Assessment
                          </button>
                          <button class="btn btn-outline-secondary btn-lg px-5 ms-3" onclick="window.close()">
                              Close Preview
                          </button>
                      </div>
                  </div>
              </div>
          </div>

          <script src="https://cdn.jsdelivr.net/npm/bootstrap@5.1.3/dist/js/bootstrap.bundle.min.js"></script>
      </body>
      </html>
    `;
    
    previewWindow.document.write(previewContent);
    previewWindow.document.close();
  };

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/exams/${exam_id}/questions`);
      setQuestions(response.data);
    } catch (error) {
      showError('Failed to fetch questions.');
    } finally {
      setLoading(false);
    }
  };

  const handleQuestionTypeSelect = (type) => {
    setSelectedQuestionType(type);
    setQuestionForm(prev => ({
      ...prev,
      question_type: type,
      options: getDefaultOptions(type)
    }));
    setShowQuestionModal(true);
  };

  const getDefaultOptions = (type) => {
    switch (type) {
      case 'true_false':
        return [
          { option_text: 'True', is_correct: false, option_order: 1 },
          { option_text: 'False', is_correct: false, option_order: 2 }
        ];
      case 'multiple_choice':
        return [
          { option_text: '', is_correct: false, option_order: 1 },
          { option_text: '', is_correct: false, option_order: 2 },
          { option_text: '', is_correct: false, option_order: 3 },
          { option_text: '', is_correct: false, option_order: 4 }
        ];
      default:
        return [];
    }
  };

  const handleAddOption = () => {
    setQuestionForm(prev => ({
      ...prev,
      options: [
        ...prev.options,
        { 
          option_text: '', 
          is_correct: false, 
          option_order: prev.options.length + 1 
        }
      ]
    }));
  };

  const handleRemoveOption = (index) => {
    if (questionForm.options.length > 2) {
      setQuestionForm(prev => ({
        ...prev,
        options: prev.options.filter((_, i) => i !== index)
          .map((option, i) => ({ ...option, option_order: i + 1 }))
      }));
    }
  };

  const handleOptionChange = (index, field, value) => {
    setQuestionForm(prev => ({
      ...prev,
      options: prev.options.map((option, i) => 
        i === index ? { ...option, [field]: value } : option
      )
    }));
  };

  const handleCorrectOptionChange = (index) => {
    if (questionForm.question_type === 'true_false' || questionForm.question_type === 'multiple_choice') {
      setQuestionForm(prev => ({
        ...prev,
        options: prev.options.map((option, i) => ({
          ...option,
          is_correct: i === index
        }))
      }));
    }
  };

  const handleSubmitQuestion = async (e) => {
    e.preventDefault();
    
    if ((questionForm.question_type === 'multiple_choice' || questionForm.question_type === 'true_false') 
        && !questionForm.options.some(opt => opt.is_correct)) {
      showError('Please select the correct answer.');
      return;
    }

    const submitData = {
      question_text: questionForm.question_text,
      question_type: questionForm.question_type,
      points: questionForm.points,
      order: editingQuestion ? editingQuestion.question_order : questions.length + 1,
      options: questionForm.options
    };

    try {
      if (editingQuestion) {
        await api.put(`/questions/${editingQuestion.question_id}`, submitData);
        showSuccess('Question updated successfully!');
      } else {
        await api.post(`/exams/${exam_id}/questions`, submitData);
        showSuccess('Question added successfully!');
      }
      
      setShowQuestionModal(false);
      resetQuestionForm();
      fetchQuestions();
    } catch (error) {
      if (error.response?.data?.errors) {
        const firstError = Object.values(error.response.data.errors)[0][0];
        showError(firstError);
      } else {
        showError('Failed to save question. Please try again.');
      }
    }
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion(question);
    setSelectedQuestionType(question.question_type);
    setQuestionForm({
      question_text: question.question_text,
      question_type: question.question_type,
      points: question.points,
      question_order: question.question_order,
      options: question.options.length > 0 ? question.options : getDefaultOptions(question.question_type)
    });
    setShowQuestionModal(true);
  };

  const handleDeleteQuestion = async (question) => {
    const confirmed = await showDeleteConfirmation();
    if (confirmed) {
      try {
        await api.delete(`/questions/${question.question_id}`);
        showSuccess('Question deleted successfully!');
        fetchQuestions();
      } catch (error) {
        showError('Failed to delete question. Please try again.');
      }
    }
  };

  const resetQuestionForm = () => {
    setEditingQuestion(null);
    setQuestionForm({
      question_text: '',
      question_type: 'multiple_choice',
      points: 1,
      question_order: 1,
      options: getDefaultOptions('multiple_choice')
    });
  };

  const renderQuestionTypeOptions = () => {
    switch (questionForm.question_type) {
      case 'multiple_choice':
      case 'true_false':
        return (
          <div>
            <Form.Label>Options</Form.Label>
            {questionForm.options.map((option, index) => (
              <div key={index} className="mb-2 d-flex align-items-center gap-2">
                <Form.Check
                  type="radio"
                  name="correct_option"
                  checked={option.is_correct}
                  onChange={() => handleCorrectOptionChange(index)}
                />
                <Form.Control
                  type="text"
                  placeholder={`Option ${index + 1}`}
                  value={option.option_text}
                  onChange={(e) => handleOptionChange(index, 'option_text', e.target.value)}
                  required
                  disabled={questionForm.question_type === 'true_false'}
                />
                {questionForm.question_type === 'multiple_choice' && questionForm.options.length > 2 && (
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleRemoveOption(index)}
                  >
                    <FaTrash />
                  </Button>
                )}
              </div>
            ))}
            {questionForm.question_type === 'multiple_choice' && (
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleAddOption}
                className="mt-2"
              >
                <FaPlus /> Add Option
              </Button>
            )}
          </div>
        );
      
      case 'short_answer':
      case 'essay':
        return (
          <Alert variant="info">
            <FaQuestionCircle className="me-2" />
            This question type will allow students to provide written answers.
          </Alert>
        );
      
      default:
        return (
          <Alert variant="warning">
            <FaQuestionCircle className="me-2" />
            This question type is coming soon in future updates.
          </Alert>
        );
    }
  };

  const getQuestionTypeIcon = (type) => {
    const questionType = questionTypes.find(qt => qt.type === type);
    return questionType ? questionType.icon : FaQuestionCircle;
  };

  const getQuestionTypeColor = (type) => {
    const questionType = questionTypes.find(qt => qt.type === type);
    return questionType ? questionType.color : '#6c757d';
  };

  // CSV Import Functions
  const downloadTemplate = () => {
    const link = document.createElement('a');
    link.href = '/templates/questions_template.csv';
    link.download = 'questions_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCsvFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'text/csv') {
      setCsvFile(file);
      setImportErrors([]);
    } else {
      showError('Please select a valid CSV file');
      setCsvFile(null);
    }
  };

  const parseCSV = (csvText) => {
    const lines = csvText.split('\n').filter(line => line.trim());
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
    const questions = [];
    const errors = [];

    for (let i = 1; i < lines.length; i++) {
      try {
        const values = [];
        let current = '';
        let inQuotes = false;
        
        for (let j = 0; j < lines[i].length; j++) {
          const char = lines[i][j];
          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === ',' && !inQuotes) {
            values.push(current.trim());
            current = '';
          } else {
            current += char;
          }
        }
        values.push(current.trim());

        if (values.length >= headers.length) {
          const question = {};
          headers.forEach((header, index) => {
            question[header] = values[index] ? values[index].replace(/"/g, '') : '';
          });

          // Validate required fields
          if (!question.question_type || !question.question_text) {
            errors.push(`Row ${i + 1}: Question type and text are required`);
            continue;
          }

          // Format the question for API
          const formattedQuestion = {
            question_type: question.question_type,
            question_text: question.question_text,
            points: parseInt(question.points) || 1,
            difficulty: question.difficulty || 'medium',
            options: []
          };

          if (question.question_type === 'multiple_choice') {
            const options = [
              { option_text: question.option_a, is_correct: question.correct_answer === 'A' },
              { option_text: question.option_b, is_correct: question.correct_answer === 'B' },
              { option_text: question.option_c, is_correct: question.correct_answer === 'C' },
              { option_text: question.option_d, is_correct: question.correct_answer === 'D' }
            ].filter(opt => opt.option_text);

            if (options.length < 2) {
              errors.push(`Row ${i + 1}: Multiple choice questions need at least 2 options`);
              continue;
            }
            formattedQuestion.options = options;
          } else if (question.question_type === 'true_false') {
            formattedQuestion.options = [
              { option_text: 'True', is_correct: question.correct_answer === 'A' },
              { option_text: 'False', is_correct: question.correct_answer === 'B' }
            ];
          } else if (question.question_type === 'short_answer' || question.question_type === 'essay') {
            formattedQuestion.correct_answer = question.correct_answer;
          }

          questions.push(formattedQuestion);
        }
      } catch (error) {
        errors.push(`Row ${i + 1}: Error parsing data`);
      }
    }

    return { questions, errors };
  };

  const handleImportQuestions = async () => {
    if (!csvFile) {
      showError('Please select a CSV file');
      return;
    }

    setImporting(true);
    setImportErrors([]);

    try {
      const fileText = await csvFile.text();
      const { questions, errors } = parseCSV(fileText);

      if (errors.length > 0) {
        setImportErrors(errors);
        setImporting(false);
        return;
      }

      if (questions.length === 0) {
        showError('No valid questions found in the CSV file');
        setImporting(false);
        return;
      }

      // Import all questions at once
      try {
        const response = await api.post(`/exams/${exam_id}/questions/import`, {
          questions: questions
        });

        if (response.status === 200) {
          const { imported_count, error_count, errors } = response.data;
          
          if (imported_count > 0) {
            showSuccess(`Successfully imported ${imported_count} questions`);
            fetchQuestions(); // Refresh the questions list
          }

          if (error_count > 0) {
            showWarning(`${error_count} questions failed to import. Check console for details.`);
            console.error('Import errors:', errors);
          }
        }
      } catch (error) {
        console.error('Error importing questions:', error);
        if (error.response?.data?.errors) {
          setImportErrors(Object.values(error.response.data.errors).flat());
          setImporting(false);
          return;
        }
        showError('Failed to import questions. Please check the file format.');
      }

      setShowImportModal(false);
      setCsvFile(null);

    } catch (error) {
      showError('Error reading CSV file');
      console.error('CSV import error:', error);
    }

    setImporting(false);
  };

  if (loading && !assessment) {
    return (
      <div style={{ 
        height: '100vh', 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div className="text-center text-white">
          <div className="spinner-border mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <h4>Loading Assessment Builder...</h4>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f8f9fa',
      fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif"
    }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #29204C 0%, #1a1530 100%)',
        borderBottom: '4px solid #68B977',
        boxShadow: '0 4px 20px rgba(41, 32, 76, 0.15)',
        position: 'sticky',
        top: 0,
        zIndex: 1000
      }}>
        <Container fluid className="px-4 py-3">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-3">
              <div style={{
                width: '56px',
                height: '56px',
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid rgba(255, 255, 255, 0.2)'
              }}>
                <FaCog color="#68B977" size={28} />
              </div>
              <div>
                <div className="d-flex align-items-center gap-2 mb-1">
                  {!isEditingTitle ? (
                    <>
                      <h3 className="mb-0" style={{ color: 'white', fontWeight: '700', fontSize: '26px' }}>
                        {assessment?.title || 'Assessment Builder'}
                      </h3>
                      <Button
                        variant="link"
                        onClick={handleTitleEdit}
                        className="p-1"
                        style={{ color: 'rgba(255,255,255,0.7)' }}
                        title="Edit Assessment Title"
                      >
                        <FaPencilAlt size={16} />
                      </Button>
                    </>
                  ) : (
                    <div className="d-flex align-items-center gap-2">
                      <Form.Control
                        type="text"
                        value={editedTitle}
                        onChange={(e) => setEditedTitle(e.target.value)}
                        style={{
                          fontSize: '26px',
                          fontWeight: '700',
                          border: '3px solid #68B977',
                          borderRadius: '10px',
                          padding: '6px 16px',
                          width: '450px',
                          background: 'rgba(255,255,255,0.95)'
                        }}
                        autoFocus
                      />
                      <Button
                        size="sm"
                        onClick={handleTitleSave}
                        style={{
                          padding: '8px 14px',
                          background: '#68B977',
                          border: 'none',
                          borderRadius: '8px'
                        }}
                      >
                        <FaCheck />
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleTitleCancel}
                        style={{
                          padding: '8px 14px',
                          background: 'rgba(255,255,255,0.2)',
                          border: 'none',
                          borderRadius: '8px',
                          color: 'white'
                        }}
                      >
                        <FaTimes />
                      </Button>
                    </div>
                  )}
                </div>
                <div className="d-flex align-items-center gap-3" style={{ fontSize: '15px', color: 'rgba(255,255,255,0.85)', fontWeight: '500' }}>
                  {assessment?.job && (
                    <>
                      <div className="d-flex align-items-center gap-1">
                        <FaBriefcase size={14} />
                        <span>{assessment.job.posting_title}</span>
                      </div>
                      <span>•</span>
                    </>
                  )}
                  <span>{questions.length} Questions</span>
                  <span>•</span>
                  <span>{questions.reduce((sum, q) => sum + parseFloat(q.points || 0), 0)} Total Points</span>
                </div>
              </div>
            </div>

            <div className="d-flex gap-2">
              <Button
                className="d-flex align-items-center gap-2"
                style={{
                  background: 'rgba(255, 255, 255, 0.15)',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  color: 'white',
                  fontWeight: '600',
                  padding: '10px 20px',
                  borderRadius: '10px',
                  transition: 'all 0.3s ease'
                }}
                onClick={() => setShowImportModal(true)}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.25)';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.15)';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                <FaCogs /> Import CSV
              </Button>

              <Button
                className="d-flex align-items-center gap-2"
                style={{
                  background: 'rgba(255, 255, 255, 0.15)',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  color: 'white',
                  fontWeight: '600',
                  padding: '10px 20px',
                  borderRadius: '10px',
                  transition: 'all 0.3s ease'
                }}
                onClick={handlePreview}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.25)';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.15)';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                <FaEye /> Preview
              </Button>

              <Button
                className="d-flex align-items-center gap-2"
                style={{
                  background: '#68B977',
                  border: 'none',
                  color: 'white',
                  fontWeight: '700',
                  padding: '10px 24px',
                  borderRadius: '10px',
                  boxShadow: '0 4px 12px rgba(104, 185, 119, 0.4)',
                  transition: 'all 0.3s ease'
                }}
                onClick={handleSave}
                onMouseEnter={(e) => {
                  e.target.style.background = '#5aa368';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 16px rgba(104, 185, 119, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#68B977';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(104, 185, 119, 0.4)';
                }}
              >
                <FaSave /> Save
              </Button>

              <Button
                onClick={() => {
                  window.close();
                  navigate('/assessments');
                }}
                className="d-flex align-items-center gap-2"
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '2px solid rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  fontWeight: '600',
                  padding: '10px 20px',
                  borderRadius: '10px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                }}
              >
                <FaTimes /> Close
              </Button>
            </div>
          </div>
        </Container>
      </div>

      {/* Main Content */}
      <Container fluid className="p-4" style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <Row className="g-4">
          {/* Question Types Sidebar */}
          <Col xl={3} lg={4}>
            <Card style={{
              border: 'none',
              borderRadius: '20px',
              boxShadow: '0 8px 32px rgba(41, 32, 76, 0.12)',
              position: 'sticky',
              top: '100px',
              overflow: 'hidden'
            }}>
              <Card.Header style={{
                background: 'linear-gradient(135deg, #29204C 0%, #1a1530 100%)',
                color: 'white',
                borderRadius: '20px 20px 0 0',
                padding: '24px',
                borderBottom: '4px solid #68B977'
              }}>
                <h5 className="mb-0 d-flex align-items-center" style={{ fontWeight: '700', fontSize: '18px' }}>
                  <FaPlus className="me-2" style={{ fontSize: '20px' }} />
                  Question Types
                </h5>
              </Card.Header>
              <Card.Body className="p-4" style={{ background: '#ffffff' }}>
                {questionTypes.map((type) => {
                  const Icon = type.icon;
                  return (
                    <Button
                      key={type.type}
                      variant="outline-light"
                      className="w-100 mb-3 text-start"
                      style={{
                        borderColor: type.color,
                        color: type.color,
                        borderRadius: '14px',
                        border: '2.5px solid',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        background: `linear-gradient(135deg, ${type.color}05 0%, ${type.color}10 100%)`,
                        padding: '16px 18px',
                        fontWeight: '600'
                      }}
                      onClick={() => handleQuestionTypeSelect(type.type)}
                      onMouseEnter={(e) => {
                        e.target.style.background = type.color;
                        e.target.style.color = 'white';
                        e.target.style.transform = 'translateX(4px) translateY(-2px)';
                        e.target.style.boxShadow = `0 6px 20px ${type.color}50`;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = `linear-gradient(135deg, ${type.color}05 0%, ${type.color}10 100%)`;
                        e.target.style.color = type.color;
                        e.target.style.transform = 'translateX(0) translateY(0)';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      <div className="d-flex align-items-center">
                        <Icon className="me-3" size={20} />
                        <div>
                          <div className="fw-bold mb-1">{type.label}</div>
                          <small style={{ opacity: 0.8 }}>{type.description}</small>
                        </div>
                      </div>
                    </Button>
                  );
                })}
              </Card.Body>
            </Card>
          </Col>

          {/* Questions List */}
          <Col xl={9} lg={8}>
            <Card style={{
              border: 'none',
              borderRadius: '20px',
              boxShadow: '0 8px 32px rgba(41, 32, 76, 0.12)',
              minHeight: '70vh',
              overflow: 'hidden'
            }}>
              <Card.Header style={{
                background: 'linear-gradient(135deg, #29204C 0%, #1a1530 100%)',
                borderRadius: '20px 20px 0 0',
                padding: '24px',
                borderBottom: '4px solid #68B977'
              }}>
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0" style={{ color: 'white', fontWeight: '700', fontSize: '20px' }}>
                    Questions ({questions.length})
                  </h5>
                  <div style={{
                    background: 'rgba(104, 185, 119, 0.2)',
                    backdropFilter: 'blur(10px)',
                    color: '#68B977',
                    padding: '10px 20px',
                    borderRadius: '12px',
                    fontWeight: '700',
                    fontSize: '16px',
                    border: '2px solid rgba(104, 185, 119, 0.3)'
                  }}>
                    Total Points: {questions.reduce((sum, q) => sum + parseFloat(q.points || 0), 0)}
                  </div>
                </div>
              </Card.Header>
              <Card.Body style={{ padding: '32px', background: '#ffffff' }}>
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border" style={{ color: '#29204C' }} role="status">
                      <span className="visually-hidden">Loading questions...</span>
                    </div>
                    <p className="mt-3" style={{ color: '#29204C' }}>Loading questions...</p>
                  </div>
                ) : questions.length === 0 ? (
                  <div className="text-center py-5">
                    <div style={{
                      width: '120px',
                      height: '120px',
                      background: 'linear-gradient(135deg, #29204C 0%, #68B977 100%)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 30px'
                    }}>
                      <FaQuestionCircle size={60} color="white" />
                    </div>
                    <h4 style={{ color: '#29204C', fontWeight: 'bold' }}>No Questions Added Yet</h4>
                    <p style={{ color: '#6c757d', fontSize: '16px' }}>
                      Start building your assessment by selecting a question type from the sidebar.
                    </p>
                  </div>
                ) : (
                  <div className="questions-list">
                    {questions.map((question, index) => {
                      const Icon = getQuestionTypeIcon(question.question_type);
                      const color = getQuestionTypeColor(question.question_type);
                      
                      return (
                        <Card key={question.question_id} className="mb-4" style={{
                          border: '2.5px solid #f0f0f0',
                          borderRadius: '16px',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = color;
                          e.currentTarget.style.boxShadow = `0 8px 24px ${color}25`;
                          e.currentTarget.style.transform = 'translateY(-4px)';
                          e.currentTarget.style.background = '#ffffff';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = '#f0f0f0';
                          e.currentTarget.style.boxShadow = 'none';
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.background = 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)';
                        }}>
                          <Card.Body style={{ padding: '28px' }}>
                            <div className="d-flex justify-content-between align-items-start">
                              <div className="flex-grow-1">
                                <div className="d-flex align-items-center gap-3 mb-3">
                                  <div
                                    style={{
                                      backgroundColor: color,
                                      color: 'white',
                                      padding: '10px 16px',
                                      borderRadius: '12px',
                                      fontSize: '15px',
                                      fontWeight: '700',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                      boxShadow: `0 4px 12px ${color}40`
                                    }}
                                  >
                                    <Icon size={16} />
                                    Q{index + 1}
                                  </div>
                                  <span style={{
                                    background: 'rgba(104, 185, 119, 0.15)',
                                    color: '#68B977',
                                    padding: '8px 14px',
                                    borderRadius: '10px',
                                    fontSize: '13px',
                                    fontWeight: '700',
                                    border: '2px solid rgba(104, 185, 119, 0.3)'
                                  }}>
                                    {question.points} {question.points === 1 ? 'Point' : 'Points'}
                                  </span>
                                  <span style={{
                                    background: 'rgba(41, 32, 76, 0.08)',
                                    color: '#29204C',
                                    padding: '8px 14px',
                                    borderRadius: '10px',
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    textTransform: 'capitalize',
                                    letterSpacing: '0.3px'
                                  }}>
                                    {(question.question_type || 'unknown').replace('_', ' ')}
                                  </span>
                                </div>
                                <h6 className="mb-3" style={{ color: '#29204C', fontWeight: '600', fontSize: '16px' }}>
                                  {question.question_text}
                                </h6>
                                
                                {(question.question_type === 'multiple_choice' || question.question_type === 'true_false') && 
                                 question.options && question.options.length > 0 && (
                                  <div className="ms-3">
                                    {question.options.map((option, optIndex) => (
                                      <div key={optIndex} className="mb-2" style={{ fontSize: '14px' }}>
                                        <span style={{ 
                                          fontWeight: 'bold',
                                          color: option.is_correct ? '#28a745' : '#6c757d',
                                          marginRight: '10px'
                                        }}>
                                          {String.fromCharCode(65 + optIndex)}.
                                        </span>
                                        <span style={{ color: option.is_correct ? '#28a745' : '#333' }}>
                                          {option.option_text}
                                          {option.is_correct && ' ✓'}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              
                              <div className="d-flex gap-2">
                                <Button
                                  variant="outline-warning"
                                  size="sm"
                                  onClick={() => handleEditQuestion(question)}
                                  style={{ 
                                    borderRadius: '8px',
                                    padding: '8px 12px'
                                  }}
                                >
                                  <FaEdit />
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleDeleteQuestion(question)}
                                  style={{ 
                                    borderRadius: '8px',
                                    padding: '8px 12px'
                                  }}
                                >
                                  <FaTrash />
                                </Button>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Question Modal */}
      <Modal 
        show={showQuestionModal} 
        onHide={() => setShowQuestionModal(false)} 
        size="lg"
        backdrop="static"
        centered
      >
        <Modal.Header 
          closeButton
          style={{
            background: 'linear-gradient(135deg, #29204C 0%, #3d2c61 100%)',
            color: 'white',
            borderRadius: '15px 15px 0 0'
          }}
        >
          <Modal.Title className="d-flex align-items-center gap-2">
            <FaPlus />
            {editingQuestion ? 'Edit Question' : 'Add New Question'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmitQuestion}>
          <Modal.Body style={{ padding: '30px' }}>
            <Row>
              <Col md={8} className="mb-3">
                <Form.Group>
                  <Form.Label style={{ fontWeight: 'bold', color: '#29204C' }}>Question Text *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={questionForm.question_text}
                    onChange={(e) => setQuestionForm({ ...questionForm, question_text: e.target.value })}
                    required
                    placeholder="Enter your question here..."
                    style={{ 
                      borderRadius: '10px',
                      border: '2px solid #e9ecef',
                      fontSize: '14px'
                    }}
                  />
                </Form.Group>
              </Col>
              
              <Col md={4} className="mb-3">
                <Form.Group>
                  <Form.Label style={{ fontWeight: 'bold', color: '#29204C' }}>Marks *</Form.Label>
                  <Form.Control
                    type="number"
                    min="0.1"
                    step="0.1"
                    value={questionForm.points}
                    onChange={(e) => setQuestionForm({ ...questionForm, points: parseFloat(e.target.value) })}
                    required
                    style={{ 
                      borderRadius: '10px',
                      border: '2px solid #e9ecef'
                    }}
                  />
                </Form.Group>
              </Col>

              <Col md={12} className="mb-3">
                <Form.Group>
                  <Form.Label style={{ fontWeight: 'bold', color: '#29204C' }}>Question Type</Form.Label>
                  <Form.Select
                    value={questionForm.question_type}
                    onChange={(e) => {
                      const newType = e.target.value;
                      setQuestionForm({
                        ...questionForm,
                        question_type: newType,
                        options: getDefaultOptions(newType)
                      });
                    }}
                    disabled={editingQuestion !== null}
                    style={{ 
                      borderRadius: '10px',
                      border: '2px solid #e9ecef'
                    }}
                  >
                    {questionTypes.map(type => (
                      <option key={type.type} value={type.type}>
                        {type.label}
                      </option>
                    ))}
                  </Form.Select>
                  {editingQuestion && (
                    <Form.Text className="text-muted">
                      Question type cannot be changed when editing.
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>

              <Col md={12}>
                {renderQuestionTypeOptions()}
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer style={{ padding: '20px 30px' }}>
            <Button 
              variant="outline-secondary" 
              onClick={() => setShowQuestionModal(false)}
              style={{ borderRadius: '8px', padding: '10px 20px' }}
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              type="submit"
              style={{ 
                borderRadius: '8px', 
                padding: '10px 20px',
                background: '#29204C',
                border: 'none'
              }}
            >
              {editingQuestion ? 'Update Question' : 'Add Question'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Import CSV Modal */}
      <Modal show={showImportModal} onHide={() => setShowImportModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Import Questions from CSV</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <Alert variant="info">
              <strong>Import Instructions:</strong>
              <ul className="mb-0 mt-2">
                <li>Download the template below to see the correct format</li>
                <li>Supported question types: multiple_choice, true_false, short_answer, essay</li>
                <li>For multiple choice: use A, B, C, D for correct_answer</li>
                <li>For true/false: use A for True, B for False</li>
                <li>For short_answer/essay: write the sample answer in correct_answer</li>
                <li>Make sure the file is saved in UTF-8 format</li>
              </ul>
            </Alert>
          </div>

          <div className="mb-3">
            <Button variant="outline-primary" onClick={downloadTemplate}>
              Download Template
            </Button>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Select CSV File</Form.Label>
            <Form.Control
              type="file"
              accept=".csv"
              onChange={handleCsvFileChange}
            />
          </Form.Group>

          {importErrors.length > 0 && (
            <Alert variant="danger">
              <strong>Import Errors:</strong>
              <ul className="mb-0 mt-2">
                {importErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowImportModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleImportQuestions}
            disabled={!csvFile || importing}
            style={{ backgroundColor: '#29204C', border: 'none' }}
          >
            {importing ? 'Importing...' : 'Import Questions'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AssessmentBuilder; 